import { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PageHeader } from "../../../../components/Headers/PageHeader";
import { SearchInput } from "../../../../components/New/SearchInput";
import { Table } from "../../../../components/New/Table";
import { Role, RoleTypes } from "../../../../@types/Role";
import * as S from "./styles";
import { wrapperRequests } from "../../../../services/api";
import { routesURL } from "../../../../services/routesUrl";
import { toast } from "react-toastify";
import { ColumnDef } from "@tanstack/react-table";
import { Button } from "../../../../components/New/Button";
import { Loading } from "../../../../components/New/Loading";
import { Modal } from "../../../../components/New/Modal";
import { DeleteIcon } from "../../../../components/New/Modal/components/DeleteIcon";
import { DeleteModal } from "../../../../components/New/Modal/components/DeleteModal";
import { MdOutlineModeEditOutline } from "react-icons/md";
import { NoDataAvailableImage } from "../../../../components/NoDataAvailableImage";
import { useMediaQuery } from "usehooks-ts";
import { useTheme } from "styled-components";
import { DrawerComponent } from "../../../../components/New/Drawer";
import { DefaultRowsPerPage } from "../../../../components/New/Table/constants";
import { useTranslation } from "react-i18next";

interface paramsType {
  page: string;
  limit: string;
  sortBy: string;
  name: string;
}

interface paramsType {
  page: string;
  limit: string;
  sortBy: string;
  name: string;
}

export function Roles() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const updatedParams = new URLSearchParams();
  const [roleSearchParams, setRoleSearchParams] = useSearchParams();

  const [roles, setRoles] = useState<RoleTypes>({
    content: [],
    name: roleSearchParams.get("name") ?? "",
    page: roleSearchParams.get("page") ?? "1",
    limit: roleSearchParams.get("limit") ?? DefaultRowsPerPage,
    sortBy: roleSearchParams.get("sortBy") ?? "",
    totalPages: roleSearchParams.get("totalPages") ?? "0",
  });
  const [loading, setLoading] = useState(false);

  const theme = useTheme();
  const matches = useMediaQuery(`(max-width: ${theme.responsiveDesign.sm})`);
  const [deleteDrawerOpen, setDeleteDrawerOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);

  const { name, page, limit, sortBy, totalPages } = roles;

  const searchAllRoles = async () => {
    setLoading(true);
    try {
      const nextPage = roleSearchParams.get("page") ?? "1";
      const nextLimit = roleSearchParams.get("limit") ?? DefaultRowsPerPage;
      const nextSortBy = roleSearchParams.get("sortBy") ?? "";
      const nextName = roleSearchParams.get("name") ?? "";
      const params: paramsType = {
        page: nextPage,
        limit: nextLimit,
        sortBy: nextSortBy,
        name: nextName,
      };
      const { data } = await wrapperRequests(
        routesURL.settings.roles.searchRoles,
        "GET",
        { params },
      );
      setRoles((state) => ({
        ...state,
        content: data.content,
        totalPages: data.totalPages || "0",
      }));
    } catch (error) {
      throw Error(String(error));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRole = async (id: string) => {
    try {
      await wrapperRequests(routesURL.settings.roles.deleteRole(id), "DELETE");
      toast.success(t("roleDeletedSuccessfully"), {
        position: "top-center",
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      setRoles((state) => ({
        ...state,
        content: state.content.filter((role) => role.id !== id),
      }));
    } catch (error) {
      console.log(error);
    }
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRoles((prev) => ({
      ...prev,
      name: event.target.value,
    }));
  };

  const updateSearchParams = () => {
    if (name) {
      updatedParams.set("name", name);
    }
    sortBy && updatedParams.set("sortBy", sortBy);
    updatedParams.set("limit", String(limit));
    updatedParams.set("page", "1");
    setRoleSearchParams(updatedParams);
  };

  const updateRows = (value: number) => {
    setRoleSearchParams((params) => {
      const updatedParams = new URLSearchParams(params.toString());
      updatedParams.set("limit", String(value));
      updatedParams.set("page", "1");
      return updatedParams;
    });
  };

  const updatePage = (value: number) => {
    setRoleSearchParams((params) => {
      const updatedParams = new URLSearchParams(params.toString());
      updatedParams.set("page", String(value));
      return updatedParams;
    });
  };

  const columns: ColumnDef<Role>[] = useMemo(
    () => [
      {
        accessorKey: "name",
        header: () => <span style={{ fontWeight: 500 }}>{t("employeeRole")}</span>,
        size: 150,
        cell: (props) => {
          const roleName = props.getValue() as string;
          return <> {roleName}</>;
        },
      },
      {
        accessorKey: "actions",
        header: () => <span style={{ fontWeight: 500 }}>{t("actions")}</span>,
        size: 150,
        cell: (props) => {
          const role = props.row.original;
          return (
            <div style={{ display: "flex", gap: "8px" }}>
              <MdOutlineModeEditOutline
                onClick={() =>
                  navigate(`/settings/roles/${role.id}`, {
                    state: { id: role.id },
                  })
                }
                size={24}
                style={{ cursor: "pointer" }}
                title={t("edit")}
              />
              {matches ? (
                <DeleteIcon
                  title={t("delete")}
                  onClick={() => {
                    setSelectedRole(role);
                    setDeleteDrawerOpen(true);
                  }}
                />
              ) : (
                <Modal
                  customButtons
                  modalContent={DeleteModal({
                    message: t("confirmDeleteRole", { name: role.name }),
                    onClick: () => handleDeleteRole(role.id),
                    isLoading: loading,
                  })}
                  title={t("roleDelete")}
                  width="21.25rem"
                >
                  <DeleteIcon title={t("delete")} />
                </Modal>
              )}
            </div>
          );
        },
      },
    ],
    [navigate, matches, loading, t],
  );

  useEffect(() => {
    const next = {
      content: [],
      name: roleSearchParams.get("name") ?? "",
      page: roleSearchParams.get("page") ?? "1",
      limit: roleSearchParams.get("limit") ?? DefaultRowsPerPage,
      sortBy: roleSearchParams.get("sortBy") ?? "",
      totalPages: roleSearchParams.get("totalPages") ?? "0",
    } as RoleTypes;

    setRoles((prev) => ({ ...prev, ...next }));
    searchAllRoles();
  }, [roleSearchParams]);

  return (
    <S.Container>
      <PageHeader
        title={t("roles")}
        description={t("rolesPageDescription")}
        customButton
      >
        <Button
          title={t("newRole")}
          onClick={() => navigate("/settings/roles/create")}
        >
          {t("newRole")}
        </Button>
      </PageHeader>
      <S.Content>
        <SearchInput
          disabled={loading}
          value={name}
          onChange={handleSearch}
          onPressEnter={updateSearchParams}
          placeholder={t("search")}
        />
        {loading && <Loading />}
        {roles.content.length === 0 && !loading && <NoDataAvailableImage />}
        {roles.content.length > 0 && !loading && (
          <S.StyledTableWrapper>
            <Table
              data={roles.content}
              columns={columns}
              columnToFilter={"roleName"}
              key={roles.content.length}
              style={{
                marginBottom: "2rem",
                width: "100%",
              }}
              pagination={{
                rowsPerPage: Number(limit),
                selectPage: updatePage,
                selectRowsPerPage: updateRows,
                page: Number(page),
                totalPages: Number(totalPages),
              }}
            />
          </S.StyledTableWrapper>
        )}
      </S.Content>

      {matches && deleteDrawerOpen && selectedRole && (
        <DrawerComponent
          open={deleteDrawerOpen}
          onOpenChange={setDeleteDrawerOpen}
          triggerContent={null}
          description=""
        >
          <div
            style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
          >
            <S.Title>{t("deleteRole")}</S.Title>
            <p>
              {t("confirmDeleteRole", { name: selectedRole.name })}
            </p>
            <Button
              title={t("confirm")}
              variant="destructive"
              width="100%"
              onClick={() => {
                handleDeleteRole(selectedRole.id);
                setDeleteDrawerOpen(false);
              }}
            />
            <Button
              title={t("cancel")}
              variant="ghost"
              width="100%"
              onClick={() => setDeleteDrawerOpen(false)}
            />
          </div>
        </DrawerComponent>
      )}
    </S.Container>
  );
}
